import { createClient } from "@supabase/supabase-js";
import { appConfig, hasSupabaseConfig } from "./config.js";
import { DemoDataService } from "./demo-service.js";
import { SupabaseDataService } from "./supabase-data-service.js";
import { requireSession, loadUserContext, requireWorkspaceMembership } from "./auth-ui.js";
import { applyUserContext, renderPresence, setConnectionState } from "./status-ui.js";
import { startLiquidUI } from "./liquid-ui.js";
import { CollaborationService } from "./collaboration-service.js";
import { startGoogleWorkspaceUI } from "./google-workspace-ui.js";

window.NAYSOR_STATUS = { setConnectionState, renderPresence, applyUserContext };
// Called when a panel that competes for right-side space opens (e.g. the Atlas
// Inspector) so it doesn't overlap an Expanded AI Dock.
window.NAYSOR_STATUS.compactIntelligenceDock = () => {
  if (document.body.classList.contains("intelligence-pinned") && !document.body.classList.contains("intelligence-minimized")) {
    setIntelligenceSizeState("compact");
  }
};

const SIDEBAR_WIDTH_KEY = "naysor-sidebar-width";
const SIDEBAR_COLLAPSED_KEY = "naysor-sidebar-collapsed";
const INTELLIGENCE_WIDTH_KEY = "naysor-intelligence-width-v56";
const INTELLIGENCE_STATE_KEY = "naysor-intelligence-size-state";
const INTELLIGENCE_MINIMIZED_KEY = "naysor-intelligence-minimized";
const INTELLIGENCE_PINNED_KEY = "naysor-intelligence-pinned";
// Three discrete states (Expanded/Compact/Rail) replace the old 210-720px continuous
// drag. Rail continues to be driven by the existing intelligence-minimized class/CSS
// (--intelligence-rail-w) — only Expanded/Compact widths are managed here.
const INTELLIGENCE_SIZE_STATES = {
  compact: { width: 240, min: 210, max: 280 },
  expanded: { width: 420, min: 360, max: 500 }
};
let activeAuthClient = null;
let authClockRecoveryAttempts = 0;

function intelligenceSizeState() {
  const saved = localStorage.getItem(INTELLIGENCE_STATE_KEY);
  if (saved && INTELLIGENCE_SIZE_STATES[saved]) return saved;
  // One-time migration: the pre-3-state build stored a single continuous width.
  const oldWidth = Number(localStorage.getItem(INTELLIGENCE_WIDTH_KEY)) || INTELLIGENCE_SIZE_STATES.expanded.width;
  const state = oldWidth <= 300 ? "compact" : "expanded";
  localStorage.setItem(INTELLIGENCE_STATE_KEY, state);
  return state;
}

function applyIntelligenceWidth(width) {
  document.documentElement.style.setProperty("--intelligence-w", width + "px");
  localStorage.setItem(INTELLIGENCE_WIDTH_KEY, String(width));
}

function setIntelligenceSizeState(state, width) {
  const spec = INTELLIGENCE_SIZE_STATES[state] || INTELLIGENCE_SIZE_STATES.expanded;
  applyIntelligenceWidth(Math.max(spec.min, Math.min(spec.max, width ?? spec.width)));
  localStorage.setItem(INTELLIGENCE_STATE_KEY, state);
}

function restoreWorkspaceLayout() {
  const savedWidth = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
  if (savedWidth >= 248 && savedWidth <= 390) {
    document.documentElement.style.setProperty("--sidebar-w", savedWidth + "px");
  }
  const state = intelligenceSizeState();
  const spec = INTELLIGENCE_SIZE_STATES[state];
  const savedIntelligenceWidth = Number(localStorage.getItem(INTELLIGENCE_WIDTH_KEY));
  const width = savedIntelligenceWidth >= spec.min && savedIntelligenceWidth <= spec.max ? savedIntelligenceWidth : spec.width;
  document.documentElement.style.setProperty("--intelligence-w", width + "px");
  document.body.classList.toggle("sidebar-collapsed", localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true");
  document.body.classList.toggle("intelligence-pinned", localStorage.getItem(INTELLIGENCE_PINNED_KEY) === "1" && localStorage.getItem("naysor-intelligence-open") === "1");
  document.body.classList.toggle("intelligence-minimized", localStorage.getItem(INTELLIGENCE_MINIMIZED_KEY) === "1");
}

function setSidebarCollapsed(collapsed) {
  document.body.classList.toggle("sidebar-collapsed", collapsed);
  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  const button = document.querySelector('[data-action="toggle-sidebar"]');
  if (button) button.setAttribute("aria-expanded", String(!collapsed));
}

function enableSidebarResize() {
  const resizer = document.getElementById("sidebarResizer");
  if (!resizer) return;
  resizer.addEventListener("pointerdown", event => {
    if (window.matchMedia("(max-width: 980px)").matches || document.body.classList.contains("sidebar-collapsed")) return;
    event.preventDefault();
    resizer.setPointerCapture(event.pointerId);
    document.body.classList.add("sidebar-resizing");
    const onMove = moveEvent => {
      const width = Math.max(248, Math.min(390, moveEvent.clientX));
      document.documentElement.style.setProperty("--sidebar-w", width + "px");
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
    };
    const onUp = () => {
      document.body.classList.remove("sidebar-resizing");
      resizer.removeEventListener("pointermove", onMove);
      resizer.removeEventListener("pointerup", onUp);
      resizer.removeEventListener("pointercancel", onUp);
    };
    resizer.addEventListener("pointermove", onMove);
    resizer.addEventListener("pointerup", onUp);
    resizer.addEventListener("pointercancel", onUp);
  });
}

function enableIntelligenceResize() {
  const resizer = document.getElementById("intelligenceDockResizer");
  if (!resizer) return;
  resizer.addEventListener("dblclick", () => {
    if (document.body.classList.contains("intelligence-minimized")) {
      document.body.classList.remove("intelligence-minimized");
      localStorage.setItem(INTELLIGENCE_MINIMIZED_KEY, "0");
    }
    setIntelligenceSizeState(intelligenceSizeState() === "expanded" ? "compact" : "expanded");
  });
  resizer.addEventListener("pointerdown", event => {
    if (window.matchMedia("(max-width: 1040px)").matches || !document.body.classList.contains("intelligence-pinned")) return;
    if (document.body.classList.contains("intelligence-minimized")) {
      document.body.classList.remove("intelligence-minimized");
      localStorage.setItem(INTELLIGENCE_MINIMIZED_KEY, "0");
      setIntelligenceSizeState("compact");
    }
    event.preventDefault();
    resizer.setPointerCapture(event.pointerId);
    document.body.classList.add("intelligence-resizing");
    const onMove = moveEvent => {
      const rawWidth = window.innerWidth - moveEvent.clientX;
      const currentSpec = INTELLIGENCE_SIZE_STATES[intelligenceSizeState()];
      // Dragging past the current state's range promotes/demotes to the neighbor state.
      if (currentSpec === INTELLIGENCE_SIZE_STATES.compact && rawWidth > currentSpec.max) {
        setIntelligenceSizeState("expanded", INTELLIGENCE_SIZE_STATES.expanded.min);
        return;
      }
      if (currentSpec === INTELLIGENCE_SIZE_STATES.expanded && rawWidth < currentSpec.min) {
        setIntelligenceSizeState("compact", INTELLIGENCE_SIZE_STATES.compact.max);
        return;
      }
      applyIntelligenceWidth(Math.max(currentSpec.min, Math.min(currentSpec.max, rawWidth)));
    };
    const onUp = () => {
      document.body.classList.remove("intelligence-resizing");
      resizer.removeEventListener("pointermove", onMove);
      resizer.removeEventListener("pointerup", onUp);
      resizer.removeEventListener("pointercancel", onUp);
    };
    resizer.addEventListener("pointermove", onMove);
    resizer.addEventListener("pointerup", onUp);
    resizer.addEventListener("pointercancel", onUp);
  });
}

restoreWorkspaceLayout();

async function createPlatform() {
  if (!hasSupabaseConfig) {
    setConnectionState("demo");
    return new DemoDataService();
  }

  const client = createClient(appConfig.supabaseUrl, appConfig.supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    realtime: {
      params: { eventsPerSecond: 10 }
    }
  });
  activeAuthClient = client;

  const session = await requireSession(client, appConfig);
  let context = await loadUserContext(client, session);
  context = await requireWorkspaceMembership(client, context);
  if (context.profile.active === false || context.membership.active === false) {
    await client.auth.signOut();
    throw new Error("비활성화된 사용자입니다. System Admin에게 문의하세요.");
  }
  setConnectionState("connecting");
  return new SupabaseDataService(client, {
    session,
    profile: context.profile,
    workspaceId: context.membership.workspace_id,
    systemRole: context.membership.system_role,
    config: appConfig
  });
}

async function boot() {
  try {
    const platform = await createPlatform();
    window.NAYSOR_PLATFORM = platform;
    applyUserContext(platform.profile, platform.systemRole || platform.profile.system_role, platform.mode);
    document.getElementById("authRoot").hidden = true;
    document.getElementById("appShell").hidden = false;
    document.body.classList.remove("booting");
    await import("../app.js");
    if (!window.NAYSOR_APP) {
      await new Promise(resolve => window.addEventListener("naysor:app-ready", resolve, { once: true }));
    }
    platform.subscribe(
      event => window.NAYSOR_APP?.handleRealtime?.(event),
      users => renderPresence(users),
      status => setConnectionState(status)
    );
    const collaboration = new CollaborationService(platform);
    await collaboration.start();
    window.NAYSOR_COLLABORATION = collaboration;
    await startGoogleWorkspaceUI(platform);
    setConnectionState(platform.mode === "demo" ? "demo" : "online");
    if (platform.profile?.must_change_password) {
      window.setTimeout(() => {
        window.NAYSOR_APP?.openProfile?.();
        window.dispatchEvent(new CustomEvent("naysor:toast", { detail: "초기 비밀번호를 사용 중입니다. My Profile에서 새 비밀번호로 변경해 주세요." }));
      }, 450);
    }
  } catch (error) {
    console.error(error);
    const root = document.getElementById("authRoot");
    root.hidden = false;
    if (/JWT issued at future/i.test(String(error?.message || error)) && activeAuthClient && authClockRecoveryAttempts < 2) {
      authClockRecoveryAttempts += 1;
      root.innerHTML = [
        '<section class="auth-card">',
        '<div class="auth-brand"><span><img src="/naysor-identity.svg" alt="NAYSOR"></span><div><strong>NAYSOR</strong><small>Project Manager</small></div></div>',
        '<h1>Synchronizing secure session</h1>',
        '<p class="auth-message warning">The authentication clock is being synchronized. The workspace will retry automatically.</p>',
        '</section>'
      ].join("");
      await new Promise(resolve => window.setTimeout(resolve, 2500));
      await activeAuthClient.auth.refreshSession();
      return boot();
    }
    root.innerHTML = [
      '<section class="auth-card">',
      '<div class="auth-brand"><span><img src="/naysor-identity.svg" alt="NAYSOR"></span><div><strong>NAYSOR</strong><small>Project Manager</small></div></div>',
      "<h1>시작할 수 없습니다</h1>",
      '<p class="auth-message error">' + String(error.message || error).replaceAll("<", "&lt;") + "</p>",
      '<button class="button auth-primary" onclick="location.reload()">다시 시도</button>',
      activeAuthClient ? '<button class="auth-link" type="button" data-auth-action="reset-session">Reset secure session</button>' : '',
      "</section>"
    ].join("");
    root.querySelector('[data-auth-action="reset-session"]')?.addEventListener("click", async event => {
      event.currentTarget.disabled = true;
      await activeAuthClient.auth.signOut({ scope: "local" });
      location.reload();
    });
  }
}

window.addEventListener("online", () => {
  setConnectionState(window.NAYSOR_PLATFORM?.mode === "demo" ? "demo" : "online");
  window.NAYSOR_PLATFORM?.reconnect?.();
});
window.addEventListener("offline", () => setConnectionState("offline"));

document.addEventListener("click", async event => {
  const action = event.target.closest("[data-action]")?.dataset.action;
  if (action === "toggle-sidebar") {
    setSidebarCollapsed(!document.body.classList.contains("sidebar-collapsed"));
  } else if (action === "toggle-user-menu") {
    const popover = document.getElementById("userPopover");
    popover.hidden = !popover.hidden;
  } else if (action === "logout") {
    if (window.NAYSOR_PLATFORM?.client) await window.NAYSOR_PLATFORM.client.auth.signOut();
    location.reload();
  } else if (action === "open-profile") {
    window.NAYSOR_APP?.openProfile?.();
    document.getElementById("userPopover").hidden = true;
  } else if (!event.target.closest("#userPopover") && !event.target.closest("#userMenuButton")) {
    document.getElementById("userPopover").hidden = true;
  }
});

enableSidebarResize();
enableIntelligenceResize();
startLiquidUI();
boot();
